import { useCallback, useEffect, useRef, useState } from 'react';
import AppLayout from '@cloudscape-design/components/app-layout';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Container from '@cloudscape-design/components/container';
import ContentLayout from '@cloudscape-design/components/content-layout';
import Header from '@cloudscape-design/components/header';
import Link from '@cloudscape-design/components/link';
import SpaceBetween from '@cloudscape-design/components/space-between';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import { beginLogin, getAlias, getIdToken } from '../auth';
import { AppHeader } from '../components/AppHeader';
import { AttachmentView } from '../components/AttachmentView';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { ConfirmDiscardModal } from '../components/ConfirmDiscardModal';
import { DeleteReportButton } from '../components/DeleteReportButton';
import { RequestDetailsButton } from '../components/RequestDetailsButton';
import { RichText } from '../components/RichText';
import { ScopeEditor } from '../components/ScopeEditor';
import { SideNav } from '../components/SideNav';
import { SUPPORT_CONTACT, APP_HEADER_ID } from '../constants';
import { formatTimestamp } from '../utils/format';
import { downloadReport } from '../utils/reportDownload';
import { getReport, NotFoundError, ReportDetail, UnauthorizedError } from '../api';
import { clearReportsList } from './reportsListCache';
import { navigate, reportDetailPath } from '../router';

type Phase = 'loading' | 'ready' | 'error';

export function ReportDetailPage({ reportId }: { reportId: string }) {
  const [phase, setPhase] = useState<Phase>('loading');
  const [errorMessage, setErrorMessage] = useState('');
  const [detail, setDetail] = useState<ReportDetail | null>(null);
  const [alias, setAlias] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [scopeDirty, setScopeDirty] = useState(false);
  const [pendingPath, setPendingPath] = useState<string | null>(null);
  const controller = useRef<AbortController | null>(null);

  const load = useCallback(async () => {
    setPhase('loading');
    setDetail(null);
    setErrorMessage('');

    const token = await getIdToken();
    if (!token) {
      await beginLogin();
      return;
    }
    setAlias(getAlias());
    setAuthenticated(true);

    controller.current?.abort();
    const ctrl = new AbortController();
    controller.current = ctrl;

    try {
      const report = await getReport(reportId, ctrl.signal);
      if (ctrl.signal.aborted) return;
      setDetail(report);
      setPhase('ready');
    } catch (err) {
      if (ctrl.signal.aborted) return;
      if (err instanceof UnauthorizedError) {
        await beginLogin();
        return;
      }
      setPhase('error');
      setErrorMessage(
        err instanceof NotFoundError
          ? 'Report not found, or outside your access scopes.'
          : err instanceof Error
            ? err.message
            : 'Could not load report.',
      );
    }
  }, [reportId]);

  useEffect(() => {
    void load();
    return () => {
      controller.current?.abort();
    };
  }, [load]);

  const onDownload = useCallback(() => {
    if (detail) downloadReport(detail);
  }, [detail]);

  const onScopesSaved = useCallback((next: string[]) => {
    setScopeDirty(false);
    setDetail((prev) => (prev ? { ...prev, scopes: next } : prev));
    clearReportsList();
  }, []);

  const onReportDeleted = useCallback(() => {
    clearReportsList();
    navigate('/');
  }, []);

  const guardedNavigate = useCallback(
    (path: string) => {
      if (scopeDirty) {
        setPendingPath(path);
        return;
      }
      navigate(path);
    },
    [scopeDirty],
  );

  // Also warn on a hard navigation away (refresh, tab close, external link) while editing.
  useEffect(() => {
    if (!scopeDirty) return undefined;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [scopeDirty]);

  return (
    <>
      <AppHeader alias={alias} authenticated={authenticated} />
      <AppLayout
        headerSelector={`#${APP_HEADER_ID}`}
        toolsHide
        navigation={<SideNav />}
        breadcrumbs={
          <Breadcrumbs
            items={[
              { text: 'Reports', href: '/' },
              { text: detail?.title || 'Report', href: reportDetailPath(reportId) },
            ]}
          />
        }
        content={
          <ContentLayout
            header={
              <Header
                variant="h1"
                actions={
                  <SpaceBetween direction="horizontal" size="xs">
                    {detail && <DeleteReportButton reportId={detail.reportId} onDeleted={onReportDeleted} />}
                    {detail && <RequestDetailsButton reportId={detail.reportId} />}
                    {detail && (
                      <Button iconName="download" onClick={onDownload}>
                        Download
                      </Button>
                    )}
                    <Button variant="link" onClick={() => guardedNavigate('/')}>
                      Back to reports
                    </Button>
                  </SpaceBetween>
                }
              >
                {detail?.title || 'Report'}
              </Header>
            }
          >
            {phase === 'loading' && <StatusIndicator type="loading">Loading report…</StatusIndicator>}
            {phase === 'error' && (
              <StatusIndicator type="error">
                {errorMessage} {SUPPORT_CONTACT}
              </StatusIndicator>
            )}
            {phase === 'ready' && detail && (
              <Container>
                <SpaceBetween size="l">
                  <ColumnLayout columns={2} variant="text-grid">
                    <div>
                      <Box variant="awsui-key-label">Published</Box>
                      <div>{formatTimestamp(detail.publishedAt)}</div>
                    </div>
                    <div>
                      <Box variant="awsui-key-label">Source</Box>
                      <div>{detail.source || '—'}</div>
                    </div>
                  </ColumnLayout>

                  {detail.tags && detail.tags.length > 0 && (
                    <div>
                      <Box variant="awsui-key-label">Tags</Box>
                      <div>{detail.tags.join(', ')}</div>
                    </div>
                  )}

                  {detail.contentUrl && (
                    <div>
                      <Box variant="awsui-key-label">Reference</Box>
                      <div>{detail.contentUrl}</div>
                    </div>
                  )}

                  {detail.supplementalUrls && detail.supplementalUrls.length > 0 && (
                    <div>
                      <Box variant="awsui-key-label">Supplemental links</Box>
                      {detail.supplementalUrls.map((url, idx) => (
                        <div key={idx}>{url}</div>
                      ))}
                    </div>
                  )}

                  <ScopeEditor
                    reportId={detail.reportId}
                    scopes={detail.scopes}
                    onSaved={onScopesSaved}
                    onDirtyChange={setScopeDirty}
                  />

                  <div>
                    <Box variant="awsui-key-label">Body</Box>
                    <div>
                      <RichText text={detail.body} />
                    </div>
                  </div>

                  {detail.attachments && detail.attachments.length > 0 && (
                    <div>
                      <Box variant="awsui-key-label">Attachments</Box>
                      <SpaceBetween size="m">
                        {detail.attachments.map((att, idx) => (
                          <AttachmentView key={att.attachmentId ?? idx} attachment={att} />
                        ))}
                      </SpaceBetween>
                    </div>
                  )}

                  <Box>
                    <Link href={window.location.href} external>
                      Permalink
                    </Link>
                  </Box>
                </SpaceBetween>
              </Container>
            )}
          </ContentLayout>
        }
      />

      <ConfirmDiscardModal
        visible={pendingPath !== null}
        onKeepEditing={() => setPendingPath(null)}
        onDiscard={() => {
          const path = pendingPath;
          setPendingPath(null);
          setScopeDirty(false);
          if (path) navigate(path);
        }}
      />
    </>
  );
}
